import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { LISTADOS, columnasVolverAContactar } from './definiciones';
import TablaListado from '../../components/Listados/TablaListado';
import AccesosListados from '../../components/Listados/AccesosListados';
import ModalAvisarVolverAContactar from '../../components/Listados/ModalAvisarVolverAContactar';
import useFiltroPersistente from '../../hooks/useFiltroPersistente';
import DatosMaestrosService from '../../services/datosMaestrosService';
import { listarTiposTurno } from '../../services/tipoTurnoService';
import CustomModal from '../../components/CustomModal';
import useCustomModal from '../../hooks/useCustomModal';
import {
    listarVolverAContactar,
    mensajeDeError,
    quitarDeVolverAContactar,
} from '../../services/volverAContactarService';
import { apellidoNombre } from '../../utils/nombrePersona';
import PageHeader from '../../components/ui/PageHeader';

const DEF = LISTADOS.volverAContactar;

// Los tres bloques, en el orden en que los devuelve el backend. "Ya avisados" nace cerrado: es
// historia, el trabajo está en los dos de arriba.
const BLOQUES = {
    PARA_AVISAR: { etiqueta: 'Para avisar', color: '#15803d' },
    MAS_ADELANTE: { etiqueta: 'Más adelante', color: '#64748b' },
    AVISADOS: { etiqueta: 'Ya avisados', color: '#1e40af', colapsadoInicial: true },
};

function bloquePorFila(row) {
    const bloque = BLOQUES[row.bloque] || BLOQUES.PARA_AVISAR;
    return {
        clave: row.bloque || 'PARA_AVISAR',
        etiqueta: bloque.etiqueta,
        color: bloque.color,
        colapsadoInicial: bloque.colapsadoInicial === true,
    };
}

/**
 * "Volver a contactar": las consultas que no avanzaron (No viable / No quiso dejar carpeta) y que
 * el operador decidió volver a buscar más adelante — al finalizar la atención o desde el
 * histórico de turnos.
 *
 * Responde dos preguntas: a quién hay que escribirle hoy (bloque "Para avisar") y a quién ya se le
 * escribió. El aviso sale por WhatsApp desde el modal, después de ver (y poder corregir) el texto.
 * Click en la fila abre la ficha de la atención original.
 */
export default function ListadoVolverAContactar() {
    const navigate = useNavigate();
    const { modalState, hideModal, showConfirm } = useCustomModal();
    // showConfirm es una función nueva en cada render: se lee desde un ref para que las columnas
    // (y con ellas la tabla) no se rearmen en cada render.
    const showConfirmRef = useRef(showConfirm);
    showConfirmRef.current = showConfirm;
    const [motivo, setMotivo] = useFiltroPersistente('listadoVolverAContactar.motivo', '');
    const [tipoTramite, setTipoTramite] = useFiltroPersistente('listadoVolverAContactar.tipo', '');
    const [motivos, setMotivos] = useState([]);
    const [tipos, setTipos] = useState([]);
    const [recargarToken, setRecargarToken] = useState(0);
    const [registroAvisar, setRegistroAvisar] = useState(null);

    useEffect(() => {
        DatosMaestrosService.getMotivosRechazo()
            .then((res) => setMotivos(Array.isArray(res?.data) ? res.data : []))
            .catch(() => setMotivos([]));
        listarTiposTurno(false).then(setTipos).catch(() => setTipos([]));
    }, []);

    // useCallback: TablaListado la usa como dependencia de su useEffect (ver ListadoCargarJauregui).
    const fetchPage = useCallback(
        (page, size, texto) => listarVolverAContactar({ motivo, tipoTramite }, page, size, texto),
        [motivo, tipoTramite]
    );

    const quitar = useCallback((row) => {
        showConfirmRef.current(
            `¿Quitar a ${apellidoNombre(row.apellido, row.nombre)} del listado? Va a quedar registrado quién lo quitó y cuándo.`,
            async () => {
                try {
                    await quitarDeVolverAContactar(row.id);
                    toast.success('Quitado del listado.');
                    setRecargarToken((t) => t + 1);
                } catch (e) {
                    toast.error(mensajeDeError(e, 'No se pudo quitar del listado.'));
                }
            },
            'Quitar de Volver a contactar',
            'warning'
        );
    }, []);

    const columnas = useMemo(
        () => columnasVolverAContactar({ onAvisar: setRegistroAvisar, onQuitar: quitar }),
        [quitar]
    );

    const alAvisar = (resultado) => {
        setRegistroAvisar(null);
        setRecargarToken((t) => t + 1);
        if (resultado?.quedoEnCola) {
            toast.warning('WhatsApp está desconectado: el aviso quedó en cola y sale cuando se reconecte.');
        } else {
            toast.success('Aviso enviado por WhatsApp.');
        }
    };

    const chipsExtra = [
        motivo && { key: 'motivo', label: motivo, onQuitar: () => setMotivo('') },
        tipoTramite && {
            key: 'tipo',
            label: tipos.find((t) => String(t.codigo) === String(tipoTramite))?.etiqueta || tipoTramite,
            onQuitar: () => setTipoTramite(''),
        },
    ].filter(Boolean);

    const estiloLabel = { fontWeight: 500, fontSize: '0.9rem', color: '#6B7280' };

    return (
        <div className="container-fluid mt-4 listado-pagina area-recepcion">
            <PageHeader
                variante="plano"
                icono={DEF.icono}
                titulo={DEF.titulo}
                descripcion={DEF.subtitulo}
            />

            <AccesosListados actual={DEF.clave} />

            <TablaListado
                claveFiltro="listadoVolverAContactar"
                columnas={columnas}
                fetchPage={fetchPage}
                onRowClick={(row) => navigate(`/recepcion/atencion/${row.idTurno}`, {
                    // La ficha usa esto para que su botón "Volver" traiga de vuelta al listado.
                    state: { volverA: { ruta: DEF.ruta, texto: 'Volver al listado' } },
                })}
                rowKey={(row) => row.id}
                size={50}
                compacto
                dependencias={[motivo, tipoTramite, recargarToken]}
                agruparPor={bloquePorFila}
                chipsExtra={chipsExtra}
                onLimpiarExtra={() => { setMotivo(''); setTipoTramite(''); }}
                filtroPlaceholder="Buscar por nombre, apellido, CUIL o teléfono"
                filtrosExtra={
                    <>
                        <div>
                            <label className="form-label" style={estiloLabel}>
                                <i className="bi bi-flag me-2"></i>Motivo de no avance
                            </label>
                            <select className="form-select" value={motivo} onChange={(e) => setMotivo(e.target.value)}>
                                <option value="">Todos</option>
                                {motivos.map((m) => (
                                    <option key={m.id} value={m.nombre}>{m.nombre}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="form-label" style={estiloLabel}>
                                <i className="bi bi-folder2-open me-2"></i>Consulta por
                            </label>
                            <select className="form-select" value={tipoTramite} onChange={(e) => setTipoTramite(e.target.value)}>
                                <option value="">Todas</option>
                                {tipos.map((t) => (
                                    <option key={t.codigo} value={t.codigo}>{t.etiqueta || t.codigo}</option>
                                ))}
                            </select>
                        </div>
                    </>
                }
            />

            <ModalAvisarVolverAContactar
                show={!!registroAvisar}
                registro={registroAvisar}
                onCerrar={() => setRegistroAvisar(null)}
                onAvisado={alAvisar}
            />

            <CustomModal {...modalState} onHide={hideModal} />
        </div>
    );
}
